import { useState } from 'react';
import { PASSWORD_MIN_LENGTH, changePasswordSchema } from '@mindpeer/auth-shared';
import { Alert, Button, Card, CardHeader, Field, Input } from '@/components/ui';
import { useZodForm } from '@/hooks/use-zod-form';
import { api } from '@/lib/api';

export function ChangePasswordPage() {
  const [done, setDone] = useState(false);

  const form = useZodForm({
    schema: changePasswordSchema,
    initialValues: { currentPassword: '', password: '', passwordConfirmation: '' },
    onSubmit: async (input) => {
      await api.changePassword(input);
      setDone(true);
      // Clear the fields so a shared screen doesn't keep them around.
      form.reset();
    },
  });

  return (
    <Card>
      <CardHeader
        title="Change password"
        description="Changing your password signs out your other devices. This one stays signed in."
      />

      <form onSubmit={form.handleSubmit} noValidate className="max-w-md space-y-5">
        {form.formError && <Alert tone="error">{form.formError}</Alert>}
        {done && !form.formError && (
          <Alert tone="success">
            Your password has been changed and your other sessions were signed out.
          </Alert>
        )}

        <Field
          label="Current password"
          htmlFor="field-currentPassword"
          error={form.errorFor('currentPassword')}
        >
          <Input
            {...form.fieldProps('currentPassword')}
            type="password"
            autoComplete="current-password"
            required
            value={form.values.currentPassword}
            onChange={(event) => form.setValue('currentPassword', event.target.value)}
          />
        </Field>

        <Field
          label="New password"
          htmlFor="field-password"
          error={form.errorFor('password')}
          hint={`At least ${PASSWORD_MIN_LENGTH} characters, including a number or symbol.`}
        >
          <Input
            {...form.fieldProps('password')}
            type="password"
            autoComplete="new-password"
            required
            value={form.values.password}
            onChange={(event) => form.setValue('password', event.target.value)}
          />
        </Field>

        <Field
          label="Confirm new password"
          htmlFor="field-passwordConfirmation"
          error={form.errorFor('passwordConfirmation')}
        >
          <Input
            {...form.fieldProps('passwordConfirmation')}
            type="password"
            autoComplete="new-password"
            required
            value={form.values.passwordConfirmation}
            onChange={(event) => form.setValue('passwordConfirmation', event.target.value)}
          />
        </Field>

        <Button type="submit" loading={form.submitting}>
          Change password
        </Button>
      </form>
    </Card>
  );
}
